import React, { useEffect, useMemo, useRef, useState } from 'react';

import { REQUEST_FROM } from 'store/reviewList/types';

import Button from '@travel/design-system/core/src/components/Button';
import FilterChip from '@travel/design-system/core/src/components/FilterChip';
import TextField from '@travel/design-system/core/src/components/TextField';
import { L10nNumber } from '@travel/i18n';
import { ArrowDownFilled, Clear, FilterFilled, Search, StarOutline } from '@travel/icons/ui';
import { Translate } from '@travel/translation';
import ProviderFilterGroup from '@travel/traveler-core/components/ProviderFilterGroup';
import { useDeviceTypes, useDialogHandler, useGetTranslation } from '@travel/traveler-core/hooks';
import { Popup } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import Dialog from 'components/CustomDialog/Dialog';
import DialogFooter from 'components/CustomDialog/DialogFooter';
import OptionsDropdown from 'components/OptionsDropdown';
import { DEFAULT_SORT_OPTION, MAX_TEXT_COUNT } from 'constants/providerReview';
import {
  paramToGroupId,
  GROUP_ID,
  REVIEW_FILTER_SORT_OPTIONS,
  SORT_OPTIONS,
} from 'constants/searchArea';
import { useHistory } from 'core/universalRouter/hooks/useHistory';
import { useFilterGroups } from 'hooks/useFilterGroups';
import useOutsideClickWithEscape from 'hooks/useOutsideClickWithEscape';
import { selectIsJRDP, selectJrdpQuery } from 'store/providerCommonHeader/selectors';
import { getFilterResults } from 'store/providerFilters/selectors';
import { fetchReviewList } from 'store/reviewList/actions';
import { getTextCount } from 'utils/validationHelper';
import { createFilterGroups } from './utils/filterGroup';
import { getProviderPath } from './utils/providerPath';

import { DEFAULT_HOST, DEFAULT_REVIEW_HOST } from 'constants/common';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import styles from './searchArea.module.scss';

import type { Companions, Purposes } from 'store/reviewList/types';
import type { Chip, FilterGroup, SortOption } from './type';

import type { Gender } from 'constants/userProfile';

import type { URLReviewSearchQuery } from 'core/types/ReviewList';

type Props = {
  query: URLReviewSearchQuery;
  params: { providerId: string };
  isLoading: boolean;
  resetPaginationOffset: () => void;
};

export default function SearchArea(props: Props) {
  const { params, query, isLoading, resetPaginationOffset } = props;
  const [isSearchMode, setIsSearchMode] = useState(false);
  const { isOpen: isFilterOpen, onOpen, onClose } = useDialogHandler(false);
  const { isPC } = useDeviceTypes();
  const providerId = params?.providerId;
  const history = useHistory();
  const queryParams = { providerId: providerId, ...query };
  const isJRDP = useAppSelector(selectIsJRDP);
  const jrdpQuery = useAppSelector(selectJrdpQuery);

  const [selectedSort, setSelectedSort] = useState<SortOption>(
    (queryParams.sortBy as SortOption) || DEFAULT_SORT_OPTION,
  );

  const [text, setText] = useState('');

  const keywordsCharCount = queryParams.keywords?.reduce((sum, k) => sum + getTextCount(k), 0) || 0;
  const textCharCount = getTextCount(text);
  const totalCharCount = keywordsCharCount + textCharCount;
  const isTextExceeded = totalCharCount > MAX_TEXT_COUNT;

  const dispatch = useAppDispatch();
  const translate = useGetTranslation();

  const filterData = useAppSelector(getFilterResults);
  const hasSyncedRef = useRef(false);
  const initialFilterGroups = useMemo<FilterGroup[]>(
    () => createFilterGroups(filterData ?? undefined),
    [filterData],
  );

  const displayedKeywords = isNotEmptyArray(queryParams.keywords)
    ? Array.from(new Set(queryParams.keywords))
    : [];

  const {
    filterGroups,
    confirmedFilterGroups,
    handleCheckboxChange,
    handleClearAll,
    handleConfirmFilters,
    handleCancelFilters,
    setSyncedFilterData,
  } = useFilterGroups(initialFilterGroups, queryParams);

  const handleCloseFilterPopup = () => {
    handleCancelFilters();
    onClose();
  };

  useEffect(() => {
    // Guard: Only run once when filterData first loads
    // ref used to avoid re-running when dependencies change
    if (hasSyncedRef.current) {
      return;
    }

    if (filterData && isNotEmptyArray(initialFilterGroups)) {
      if (!hasSyncedRef.current) {
        setSyncedFilterData(queryParams);
        hasSyncedRef.current = true; // Mark as synced
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData, initialFilterGroups]);

  const updateReviewList = (query, shouldUpdate = false) => {
    setSyncedFilterData(query);
    resetPaginationOffset();
    const path = getProviderPath(providerId, {
      ...query,
      ...(isJRDP && {
        jrdp: '1',
        jrdpQuery: jrdpQuery,
      }),
    });

    // redirect to review domain when we apply sort or filter on travel domain
    if (window.location.hostname === DEFAULT_HOST) {
      const newUrl = new URL(path, DEFAULT_REVIEW_HOST).toString();

      window.location.replace(newUrl);

      return;
    }

    // to prevent re-rendering the whole page
    history?.push(path, { shouldUpdate });

    dispatch(fetchReviewList(query));
  };

  const handleSearchAndFetch = () => {
    if (isTextExceeded) return;
    const textKeywords = text
      .split(/\s+/)
      .map((kw) => kw.trim())
      .filter((kw) => kw.length > 0);
    const uniqueKeywords = Array.from(new Set([...displayedKeywords, ...textKeywords]));
    const searchQuery = {
      ...queryParams,
      keywords: uniqueKeywords,
      requestFrom: REQUEST_FROM.PROVIDER_REVIEW,
    };

    updateReviewList(searchQuery);
    setText('');
  };

  const handleSortChange = (sort: SortOption) => {
    setSelectedSort(sort);

    const sortQuery = {
      ...queryParams,
      sortBy: sort,
      keywords: queryParams.keywords,
      requestFrom: REQUEST_FROM.PROVIDER_REVIEW,
    };

    updateReviewList(sortQuery);
  };

  const handleConfirmFiltersAndFetch = (onClose: () => void) => {
    handleConfirmFilters(onClose, (latestConfirmedGroups) => {
      const selectedFilters = latestConfirmedGroups.reduce(
        (acc, group) => {
          const selected = group.features.filter((f) => f.selected).map((f) => f.featureId);

          if (selected.length > 0) {
            acc[group.groupId] = selected;
          }

          return acc;
        },
        {} as Record<string, string[]>,
      );
      const filterQuery = {
        ...queryParams,
        offset: 0,
        limit: 20,
        overallScores: selectedFilters.ratings ?? [],
        purposes: (selectedFilters.purposes ?? []) as Purposes[],
        companions: (selectedFilters.companions ?? []) as Companions[],
        additionalInformation: selectedFilters.additionInformation ?? [],
        ageRanges: selectedFilters.ageRanges ?? [],
        genders: (selectedFilters.genders ?? []) as Gender[],
        stayedMonths: selectedFilters.availableByMonths ?? [],
        topics: selectedFilters.topics ?? [],
        keywords: queryParams.keywords,
        requestFrom: REQUEST_FROM.PROVIDER_REVIEW,
      };

      updateReviewList(filterQuery);
    });
  };

  const handleRemoveChipAndFetch = (groupId: string, featureId: string) => {
    const paramKey = Object.entries(paramToGroupId).find(([, gid]) => gid === groupId)?.[0];

    if (!paramKey) return;

    const prev = queryParams[paramKey];
    const next = prev.filter((id) => id !== featureId);

    const queryRemoveFilter = {
      ...queryParams,
      [paramKey]: next,
      keywords: queryParams.keywords,
      requestFrom: REQUEST_FROM.PROVIDER_REVIEW,
    };

    updateReviewList(queryRemoveFilter);
  };

  const handleRemoveKeywordAndFetch = (keyword: string) => {
    const queryRemoveSearch = {
      ...queryParams,
      keywords: displayedKeywords.filter((k) => k !== keyword),
      requestFrom: REQUEST_FROM.PROVIDER_REVIEW,
    };

    updateReviewList(queryRemoveSearch);
  };

  const handleTopicChipClick = (topicName: string) => {
    handleCheckboxChange(GROUP_ID.TOPICS, topicName);

    const prevTopics = queryParams.topics || [];
    const isSelected = prevTopics.includes(topicName);

    const updatedTopics = isSelected
      ? prevTopics.filter((t) => t !== topicName)
      : [...prevTopics, topicName];

    const nextQuery = {
      ...queryParams,
      topics: updatedTopics,
      keywords: queryParams.keywords,
      requestFrom: REQUEST_FROM.PROVIDER_REVIEW,
    };

    updateReviewList(nextQuery);
  };

  const selectedChips =
    confirmedFilterGroups?.reduce<Chip[]>((selectedChips, group) => {
      if (group.groupId === GROUP_ID.TOPICS) return selectedChips;
      group.features.forEach((feature) => {
        if (feature.selected) {
          selectedChips.push({
            groupId: group.groupId,
            groupName: group.groupName,
            featureId: feature.featureId,
            name: feature.name,
          });
        }
      });

      return selectedChips;
    }, []) ?? [];

  const isChip = selectedChips.length > 0 || displayedKeywords.length > 0;

  const filterTagContent = (
    <div className={styles.filterTagWrapper}>
      {filterGroups.map((group) => (
        <div key={group.groupId} className={styles.filterGroupWrapper}>
          {/* title */}
          <div className={styles.filterGroupTitle}>{group.groupName}</div>

          <div className={styles.featureButtonSection}>
            {group.features.map((feature) => (
              <FilterChip
                key={feature.featureId}
                className={cx(styles.featureButton, feature.selected && styles.selected)}
                isSelected={feature.selected}
                value={feature.featureId}
                onClick={(value) => handleCheckboxChange(group.groupId, value as string)}
                size={isPC ? 'small' : 'medium'}
                isDisabled={feature.isDisabled}
              >
                {group.groupId === GROUP_ID.RATINGS && (
                  <FilterChip.Icon>
                    <StarOutline size={20} color="currentColor" />
                  </FilterChip.Icon>
                )}
                <FilterChip.Label>{<Translate id={feature.name} />}</FilterChip.Label>
                {group.groupId === GROUP_ID.TOPICS && feature.count !== undefined && (
                  <>
                    <FilterChip.Label>{feature.name}</FilterChip.Label>
                    <FilterChip.Count>
                      <L10nNumber value={feature.count} />
                    </FilterChip.Count>
                  </>
                )}
              </FilterChip>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const searchBoxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useOutsideClickWithEscape(searchBoxRef, inputRef, () => !isPC && setIsSearchMode(false));

  const handleTextKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchAndFetch();
      !isPC && setIsSearchMode(false);
    }
  };
  const searchBox = (
    <div
      className={cx(styles.searchBoxWrapper, isSearchMode ? styles.fullWidth : '')}
      data-testid="searchBox-wrapper"
      ref={searchBoxRef}
    >
      <TextField className={styles.searchTextFieldWrapper}>
        <TextField.InputWrapper hasError={isTextExceeded} className={styles.searchTextField}>
          <TextField.LeadingIcon>
            <Search size={20} data-testid="searchArea-searchIcon" />
          </TextField.LeadingIcon>

          <TextField.Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={translate({ id: 'Review.Filter.Keyword.Placeholder' })}
            className={styles.textInput}
            forwardRef={inputRef}
            enterKeyHint="search"
            onKeyDown={handleTextKeyDown}
            name="searchArea"
          />

          {text && <TextField.ClearButton handleClearClick={() => setText('')} />}
        </TextField.InputWrapper>
        {isTextExceeded && (
          <TextField.HelperWrapper>
            <TextField.Error
              hasError={isTextExceeded}
              className={styles.alertContentWrapper}
              data-testid="searchArea-alert-wrapper"
            >
              <Translate id="Review.Filter.Keyword.Validation" />
            </TextField.Error>
          </TextField.HelperWrapper>
        )}
      </TextField>
      {isPC && (
        <Button
          onClick={handleSearchAndFetch}
          className={styles.searchButton}
          isLoading={isLoading}
          data-testid="searchArea-searchButton"
        >
          {!isLoading && (
            <Button.Label>
              <Translate id="Review_Common.Search" />
            </Button.Label>
          )}
        </Button>
      )}
    </div>
  );

  const { isOpen: isMenuOpen, onClose: onCloseMenu, onToggle } = useDialogHandler(false);

  const sortButtonContent = (
    <>
      <div className={styles.sortButtonContent}>{SORT_OPTIONS[selectedSort]}</div>
      <ArrowDownFilled size={20} className={styles.dropdownIcon} />
    </>
  );

  const sortOptionsWithHandler = REVIEW_FILTER_SORT_OPTIONS.map((option) => ({
    ...option,
    onClickHandler: () => handleSortChange(option.id),
  }));

  return (
    <div className={styles.wrapper} data-testid="searchArea-wrapper">
      <div className={styles.searchAreaWrapper}>
        <div className={cx(styles.searchArea, isSearchMode && styles.fullWidth)}>
          {!isSearchMode ? (
            <>
              {!isPC && (
                <Button
                  className={styles.button}
                  variant="outlined"
                  onClick={() => setIsSearchMode(true)}
                  data-testid="filterButton-search-button"
                  aria-label="Open search box"
                >
                  <Button.Icon>
                    <Search size={22} className={styles.searchIcon} />
                  </Button.Icon>
                </Button>
              )}
              <div className={styles.filterButtonWrapper}>
                <Button
                  className={styles.button}
                  variant="outlined"
                  onClick={isFilterOpen ? onClose : onOpen}
                  data-testid="filterButton-filter-button"
                >
                  <Button.Icon>
                    <FilterFilled size={20} />
                  </Button.Icon>
                  <Button.Label>
                    {(isPC || !isChip) && <Translate id="Review_Common.Filter" />}
                  </Button.Label>
                </Button>
                {isPC ? (
                  <Popup
                    isOpen={isFilterOpen}
                    onClose={handleCloseFilterPopup}
                    className={styles.popupWrapper}
                  >
                    <div className={styles.popupContent}>
                      {filterGroups.map((filter) => (
                        <ProviderFilterGroup
                          className={styles.filterGroup}
                          titleClassName={styles.filterTitleContainer}
                          optionTextClassName={styles.optionText}
                          key={filter.groupId}
                          id={filter.groupId}
                          options={filter.features.map((feature) => ({
                            ...feature,
                            name:
                              filter.groupId === GROUP_ID.TOPICS
                                ? feature.name
                                : translate({ id: feature.name }),
                          }))}
                          title={filter.groupName}
                          onChange={handleCheckboxChange}
                          maxVisibleCount={filter.features.length}
                        />
                      ))}
                    </div>

                    <DialogFooter
                      buttonLabelSecondary={<Translate id="Review_Common.Clear" />}
                      buttonLabelPrimary={<Translate id="Review_Common.Confirm" />}
                      onClickPrimary={() => handleConfirmFiltersAndFetch(onClose)}
                      onClickSecondary={handleClearAll}
                      actionButtonsClassName={styles.dialogFooterButton}
                    />
                  </Popup>
                ) : (
                  <Dialog
                    isOpen={isFilterOpen}
                    onClose={handleCloseFilterPopup}
                    title={<Translate id="Review_Common.Filter" />}
                    buttonLabelSecondary={<Translate id="Review_Common.Clear" />}
                    buttonLabelPrimary={<Translate id="Review_Common.Confirm" />}
                    onClickPrimary={() => handleConfirmFiltersAndFetch(onClose)}
                    onClickSecondary={handleClearAll}
                    contentClassName={styles.dialogContent}
                    isRightCloseButton={false}
                    hasAnimation={true}
                  >
                    {filterTagContent}
                  </Dialog>
                )}
              </div>
              {isPC && (
                <>
                  <OptionsDropdown
                    trigger={sortButtonContent}
                    positionX="left"
                    shouldEnableCheckmark
                    dropDownOptions={sortOptionsWithHandler}
                    menuTriggerClass={cx(styles.button, styles.sortButton)}
                    dropdownWrapperClass={styles.tooltip}
                    defaultSelectedId={selectedSort}
                  />
                  {searchBox}
                </>
              )}
            </>
          ) : (
            searchBox
          )}
        </div>
        {!isSearchMode && (
          <div className={cx(styles.selectedChipsWrapper, isMenuOpen ? styles.showPopup : '')}>
            {!isPC && (
              <OptionsDropdown
                trigger={sortButtonContent}
                positionX={isChip ? 'left' : 'right'}
                shouldEnableCheckmark
                dropDownOptions={sortOptionsWithHandler}
                menuTriggerClass={cx(styles.button, styles.sortButton)}
                dropdownWrapperClass={styles.tooltip}
                isOpen={isMenuOpen}
                onToggle={onToggle}
                onClose={onCloseMenu}
                defaultSelectedId={selectedSort}
                shouldEnableTouchInteraction
              />
            )}
            {isChip && (
              <>
                {displayedKeywords.map((keyword) => (
                  <FilterChip
                    key={keyword}
                    isSelected
                    value={keyword}
                    onClick={() => handleRemoveKeywordAndFetch(keyword)}
                    size={isPC ? 'small' : 'medium'}
                    className={styles.filterChip}
                  >
                    <FilterChip.Label>{keyword}</FilterChip.Label>
                    <FilterChip.Icon>
                      <Clear size={20} />
                    </FilterChip.Icon>
                  </FilterChip>
                ))}
                {selectedChips.map((chip) => (
                  <FilterChip
                    key={chip.featureId}
                    isSelected
                    value={chip.featureId}
                    className={styles.filterChip}
                    onClick={() => handleRemoveChipAndFetch(chip.groupId, chip.featureId)}
                    size={isPC ? 'small' : 'medium'}
                  >
                    {chip.groupId === GROUP_ID.RATINGS && (
                      <FilterChip.Icon>
                        <StarOutline size={20} color="currentColor" />
                      </FilterChip.Icon>
                    )}
                    <FilterChip.Label>
                      <Translate id={chip.name} />
                    </FilterChip.Label>
                    <FilterChip.Icon>
                      <Clear size={20} />
                    </FilterChip.Icon>
                  </FilterChip>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {isNotEmptyArray(filterData?.topics) && (
        <div className={styles.popularTopicsWrapper}>
          {filterData.topics.map((topic) => {
            const popularGroup = confirmedFilterGroups.find((g) => g.groupId === GROUP_ID.TOPICS);
            const feature = popularGroup?.features.find((f) => f.featureId === topic.topicName);

            return (
              <FilterChip
                key={topic.topicName}
                isSelected={!!feature?.selected}
                value={topic.topicName}
                onClick={(_, _event) => {
                  if (popularGroup && feature) {
                    handleTopicChipClick(topic.topicName);
                  }
                }}
                size={isPC ? 'small' : 'medium'}
                className={styles.filterChip}
              >
                <FilterChip.Label>{topic.topicName}</FilterChip.Label>
                <FilterChip.Count>
                  <L10nNumber value={topic.count} />
                </FilterChip.Count>
              </FilterChip>
            );
          })}
        </div>
      )}
    </div>
  );
}
